import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Button, Modal, type ModalProps } from 'flowbite-react';
import { CheckIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { useApi } from '../../../api/useApi.hook';
import { formatSeconds } from '../../../editor/utils/string.utils';
import { pathWithParams } from '../../../lib/routing.utils';
import { RouteIndex } from '../../routeIndex';

export interface CreditPaymentCheckModalProps extends ModalProps {
    orderId: string;
}

export const CreditPaymentCheckModal = ({
    orderId,
    show,
    onClose
}: CreditPaymentCheckModalProps) => {
    const api = useApi();
    const navigate = useNavigate();
    const [secondsRemaining, setSecondsRemaining] = useState(600);

    const { data: orderStatus } = useQuery({
        queryKey: ['creditPurchaseOrderStatus', orderId],
        queryFn: async () => {
            return api.v1.credits.getPurchaseOrderStatus(orderId);
        },
        refetchInterval: 5000,
    });

    const handleContinueClick = useCallback(() => {
        onClose?.();
        navigate(pathWithParams(RouteIndex.creditPaymentReturn, { orderId }));
    }, [orderId, navigate, onClose]);

    useEffect(() => {
        const interval = setInterval(() => {
            setSecondsRemaining((countdown) => Math.max(0, countdown - 1));
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    const paymentReceived = orderStatus?.status === 'CAPTURED';

    return (
        <Modal show={show} size="sm" onClose={onClose} popup>
            <Modal.Header />
            <Modal.Body>
                {!paymentReceived && (
                    <div className='flex flex-col items-center gap-6'>
                        <div className={clsx(
                            'relative justify-center w-[8rem] aspect-square rounded-full text-2xl font-bold border border-gray-200'
                        )}>
                            <div className='absolute left-1 top-1 right-1 bottom-1 rounded-full [background:conic-gradient(#3498db_0deg_270deg,transparent_270deg_360deg)] [animation:spin_3s_linear_infinite]' />
                            <div className='absolute left-2 top-2 right-2 bottom-2 rounded-full flex flex-row items-center justify-center bg-white text-gray-700'>
                                {formatSeconds(secondsRemaining)}
                            </div>
                        </div>
                        {secondsRemaining > 0 && (
                            <>
                                <p className='text-xl font-semibold'>Čekáme na příjetí platby...</p>
                                <p className='text-sm text-gray-600 text-center font-medium'>Zpracování okamžité platby může trvat&nbsp;až&nbsp;10&nbsp;minut.</p>
                            </>
                        )}
                        {secondsRemaining === 0 && (
                            <>
                                <p className='text-xl font-semibold'>Platba stále nedorazila</p>
                                <p className='text-sm text-gray-600 text-center font-medium'>Zkontrolujte prosím, zda jste použili okamžitou platbu a zadali správně variabilní symbol.</p>
                                <p className='text-sm text-gray-600 text-center font-medium'>Pokud Vaše banka okamžitou platbu nepodporuje, zpracování platby může trvat i&nbsp;více jak 24 hodin.</p>
                            </>
                        )}
                        <Button color="gray" onClick={onClose}>Zavřít</Button>
                    </div>
                )}
                {paymentReceived && (
                    <div className='flex flex-col items-center gap-6'>
                        <div className={clsx(
                            'relative justify-center w-[8rem] aspect-square rounded-full text-2xl font-bold border-[3px] border-lime-600',
                            'flex flex-row items-center justify-center text-lime-600 pt-2'
                        )}>
                            <CheckIcon size={64} />
                        </div>
                        <p className='text-xl font-semibold text-lime-700'>Vaše platba byla přijata.</p>
                        <Button color="primary" size='lg' onClick={handleContinueClick}>Pokračovat</Button>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
};
